import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Star, 
  ShoppingBag, 
  Clock,
  Heart,
  Repeat,
  Award
} from "lucide-react";
import PartnerLayout from "./PartnerLayout";

interface SalesStats {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersThisMonth: number;
  revenueThisMonth: number;
  monthOverMonthGrowth: number;
}

interface LoyaltyStats {
  totalCustomers: number;
  repeatCustomers: number;
  repeatRate: number;
  vipCustomers: number;
  averageVisitsPerCustomer: number;
}

interface TopCustomer {
  id: string;
  name: string;
  avatar?: string;
  totalOrders: number;
  totalSpent: number;
  lastVisit: string;
}

interface PeakHour {
  hour: number;
  orders: number;
  percentage: number;
}

interface MenuSentiment {
  itemName: string;
  mentions: number;
  positivePercentage: number;
  sentiment: 'positive' | 'neutral' | 'negative';
}

export default function PartnerAnalytics() {
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  
  const { data: salesStats, isLoading: salesLoading } = useQuery<SalesStats>({
    queryKey: ['/api/partner/analytics/sales', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: loyaltyStats, isLoading: loyaltyLoading } = useQuery<LoyaltyStats>({
    queryKey: ['/api/partner/analytics/loyalty', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: topCustomers, isLoading: customersLoading } = useQuery<TopCustomer[]>({
    queryKey: ['/api/partner/analytics/top-customers', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: peakHours, isLoading: peakLoading } = useQuery<PeakHour[]>({
    queryKey: ['/api/partner/analytics/peak-hours', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: menuSentiment, isLoading: sentimentLoading } = useQuery<MenuSentiment[]>({
    queryKey: ['/api/partner/analytics/menu-sentiment', restaurantId],
    enabled: !!restaurantId,
  });
  
  const formatHour = (hour: number) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h = hour % 12 || 12;
    return `${h}${ampm}`;
  };
  
  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-analytics-title">Analytics</h1>
          <p className="text-muted-foreground">Insights into your restaurant's performance</p>
        </div>
        
        <Tabs defaultValue="sales">
          <TabsList>
            <TabsTrigger value="sales" data-testid="tab-sales">
              <ShoppingBag className="h-4 w-4 mr-1" />
              Sales
            </TabsTrigger>
            <TabsTrigger value="loyalty" data-testid="tab-loyalty">
              <Heart className="h-4 w-4 mr-1" />
              Loyalty
            </TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">
              <TrendingUp className="h-4 w-4 mr-1" />
              Trends
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="sales" className="mt-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                  <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {salesLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    <>
                      <div className="text-2xl font-bold" data-testid="text-total-orders">
                        {(salesStats?.totalOrders || 0).toLocaleString()}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        +{salesStats?.ordersThisMonth || 0} this month
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  {salesLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    <>
                      <div className="text-2xl font-bold" data-testid="text-total-revenue">
                        ${(salesStats?.totalRevenue || 0).toLocaleString()}
                      </div>
                      <div className="flex items-center gap-1 text-xs">
                        {(salesStats?.monthOverMonthGrowth || 0) >= 0 ? (
                          <TrendingUp className="h-3 w-3 text-green-500" />
                        ) : (
                          <TrendingDown className="h-3 w-3 text-red-500" />
                        )}
                        <span className={salesStats?.monthOverMonthGrowth && salesStats.monthOverMonthGrowth >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {Math.abs(salesStats?.monthOverMonthGrowth || 0).toFixed(1)}% vs last month
                        </span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Order Value</CardTitle>
                  <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {salesLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    <div className="text-2xl font-bold" data-testid="text-avg-order">
                      ${(salesStats?.averageOrderValue || 0).toFixed(2)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Peak Hours</CardTitle>
                <CardDescription>When customers order the most</CardDescription>
              </CardHeader>
              <CardContent>
                {peakLoading ? (
                  <Skeleton className="h-32 w-full" />
                ) : peakHours && peakHours.length > 0 ? (
                  <div className="flex items-end gap-1 h-32">
                    {peakHours.map((hour) => (
                      <div 
                        key={hour.hour} 
                        className="flex-1 flex flex-col items-center gap-1"
                        title={`${formatHour(hour.hour)}: ${hour.orders} orders`}
                      >
                        <div 
                          className="w-full bg-primary/20 rounded-t transition-all hover:bg-primary/30"
                          style={{ height: `${hour.percentage}%`, minHeight: '4px' }}
                        />
                        <span className="text-[10px] text-muted-foreground">{formatHour(hour.hour)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No order data available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="loyalty" className="mt-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {loyaltyLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    <div className="text-2xl font-bold" data-testid="text-total-customers">
                      {(loyaltyStats?.totalCustomers || 0).toLocaleString()}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Repeat Customers</CardTitle>
                  <Repeat className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {loyaltyLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    <>
                      <div className="text-2xl font-bold" data-testid="text-repeat-customers">
                        {(loyaltyStats?.repeatCustomers || 0).toLocaleString()}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {(loyaltyStats?.repeatRate || 0).toFixed(1)}% repeat rate
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">VIP Customers</CardTitle>
                  <Award className="h-4 w-4 text-yellow-500" />
                </CardHeader>
                <CardContent>
                  {loyaltyLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    <div className="text-2xl font-bold" data-testid="text-vip-customers">
                      {(loyaltyStats?.vipCustomers || 0).toLocaleString()}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Visits</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {loyaltyLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    <div className="text-2xl font-bold" data-testid="text-avg-visits">
                      {(loyaltyStats?.averageVisitsPerCustomer || 0).toFixed(1)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Top Customers</CardTitle>
                <CardDescription>Your most loyal patrons</CardDescription>
              </CardHeader>
              <CardContent>
                {customersLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : topCustomers && topCustomers.length > 0 ? (
                  <div className="space-y-3">
                    {topCustomers.map((customer, idx) => (
                      <div 
                        key={customer.id} 
                        className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                        data-testid={`customer-${customer.id}`}
                      >
                        <span className="text-lg font-bold text-muted-foreground w-6">#{idx + 1}</span>
                        <div className="flex-1">
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Last visit: {new Date(customer.lastVisit).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{customer.totalOrders} orders</p>
                          <p className="text-xs text-muted-foreground">${customer.totalSpent.toFixed(2)} spent</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No customer data yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="trends" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Menu Item Sentiment</CardTitle>
                <CardDescription>What customers are saying about your dishes</CardDescription>
              </CardHeader>
              <CardContent>
                {sentimentLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : menuSentiment && menuSentiment.length > 0 ? (
                  <div className="space-y-3">
                    {menuSentiment.map((item) => (
                      <div 
                        key={item.itemName} 
                        className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{item.itemName}</p>
                          <p className="text-xs text-muted-foreground">{item.mentions} mentions in reviews</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                item.sentiment === 'positive' ? 'bg-green-500' :
                                item.sentiment === 'negative' ? 'bg-red-500' : 'bg-yellow-500'
                              }`}
                              style={{ width: `${item.positivePercentage}%` }}
                            />
                          </div>
                          <Badge variant={
                            item.sentiment === 'positive' ? 'default' :
                            item.sentiment === 'negative' ? 'destructive' : 'secondary'
                          }>
                            {item.positivePercentage}% positive
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Not enough review data to analyze menu sentiment yet
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PartnerLayout>
  );
}
